import { DestroyRef, inject, Injectable, signal } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { MenuItem } from 'primeng/api'
import { filter } from 'rxjs/operators'
import { getMenuItems, ToolbarData } from '@app/layout/toolbar/toolbar.config'
import { PermissionService } from '@core/auth/services/permission.service'
import { CategoryService } from '@core/auth/services/category.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { SelectedInsuredService } from '@app/layout/sidebar/services/selected-insured.service'
import { DocumentPreviewService } from '@features/certificate-preview/services/document-preview.service'

@Injectable({ providedIn: 'root' })
export class ToolbarService {
  private readonly router = inject(Router)
  private readonly permissionService = inject(PermissionService)
  private readonly categoryService = inject(CategoryService)
  private readonly destroyRef = inject(DestroyRef)

  private readonly mediaQuery = globalThis.matchMedia('(min-width: 1201px)')
  private readonly isDesktop = signal(this.mediaQuery.matches)
  private readonly currentUrl = signal<string>(this.router.url)
  private readonly route = inject(ActivatedRoute)
  private readonly selectedInsured = inject(SelectedInsuredService)
  private readonly documentPreviewService = inject(DocumentPreviewService)

  constructor() {
    this.mediaQuery.addEventListener('change', (e) => {
      this.isDesktop.set(e.matches)
    })

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef) // Use modern, signal-based unsubscription
    ).subscribe((event: NavigationEnd) => {
      this.currentUrl.set(event.urlAfterRedirects)
    })
  }

  public getFilteredItems(onToggle: () => void, closeDrawer: () => void): MenuItem[] {
    const rawItems = getMenuItems(
      onToggle,
      closeDrawer,
      this.permissionService,
      this.router,
      () => this.getInsuredUuid(),
      () => this.getInsuredUuidFromRoute(),
      () => this.getUuidFromRoute('certificate/:uuid'),
      uuid => this.documentPreviewService.openPreview(uuid)
    )
    return this.applyMenuItemModifications(rawItems, this.currentUrl())
  }

  private getInsuredUuid(): string | null {
    return this.getInsuredUuidFromRoute() ?? this.selectedInsured.selectedUuid()
  }

  private getInsuredUuidFromRoute(): string | null {
    return this.getUuidFromRoute('insured/:uuid')
  }

  private getUuidFromRoute(path: string): string | null {
    let route: ActivatedRoute | null = this.route.root
    while (route) {
      if (route.snapshot.routeConfig?.path === path) {
        return route.snapshot.paramMap.get('uuid')
      }
      route = route.firstChild
    }
    return null
  }

  private applyMenuItemModifications(items: MenuItem[], url: string): MenuItem[] {
    return items.map(item => {
      const newItem = { ...item }
      const itemData = newItem['data'] as ToolbarData

      if (itemData?.testId === 'toolbar-menuitem-insured-add-overrides') {
        const cat = this.categoryService.activeCategory()
        const canEdit = this.permissionService.canEdit(cat)
        newItem.disabled = !url.includes('/insured') || !canEdit
      }

      if ((itemData?.desktopOnly) === true) {
        newItem.disabled = !this.isDesktop() || !this.permissionService.hasRedactRights()
      }

      if (newItem.items && newItem.items.length > 0) {
        newItem.items = this.applyMenuItemModifications(newItem.items, url)
      }

      return newItem
    })
  }
}
